import React, { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { API } from "../App";
import { useBalance } from "../contexts/BalanceContext";
import { useTheme } from "../contexts/ThemeContext";
import { toast } from "sonner";
import { Wallet, CreditCard, X, Check, Zap } from "lucide-react";
import { isIOSApp } from "../hooks/useIsMobileApp";

const AddFundsModal = ({ open, onClose }) => {
  const { currentBalance, topupOptions, overageRates, refetch } = useBalance();
  const { isDark } = useTheme();
  const [selectedAmount, setSelectedAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState("");
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!open) return null;
  // No wallet top-up purchases inside the iOS app (Apple Guideline 3.1.1)
  if (isIOSApp()) return null;

  const amount = isCustom ? parseFloat(customAmount) || 0 : selectedAmount;

  const handleAddFunds = async () => {
    if (amount < 5) {
      toast.error("Minimum top-up is $5.00");
      return;
    }
    if (amount > 500) {
      toast.error("Maximum top-up is $500.00");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/account/add-funds`, {
        amount,
        origin_url: window.location.origin,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create checkout");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100]" data-testid="add-funds-modal" onClick={onClose}>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
      {/* Scrollable centering wrapper */}
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          {/* Modal card - stop click propagation */}
          <div
            onClick={(e) => e.stopPropagation()}
            className={`relative w-full max-w-md rounded-2xl border shadow-2xl ${
              isDark ? 'bg-[#0c0c18] border-white/10' : 'bg-white border-gray-200'
            }`}
          >
        {/* Header */}
        <div className={`flex items-center justify-between p-5 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FFCC00]/15 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-[#FFCC00]" />
            </div>
            <div>
              <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Add Funds</h2>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                Current balance: <span className="font-semibold text-[#FFCC00]">${currentBalance.toFixed(2)}</span>
              </p>
            </div>
          </div>
          <button onClick={onClose} className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-white/10 text-white/60' : 'hover:bg-gray-100 text-gray-400'}`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Amount Selection */}
        <div className="p-5 space-y-4">
          <p className={`text-sm font-medium ${isDark ? 'text-white/70' : 'text-gray-600'}`}>Select amount</p>
          <div className="grid grid-cols-3 gap-2">
            {topupOptions.map((opt) => (
              <button
                key={opt}
                onClick={() => { setSelectedAmount(opt); setIsCustom(false); }}
                className={`py-3 px-2 rounded-xl text-sm font-bold transition-all ${
                  !isCustom && selectedAmount === opt
                    ? 'bg-[#FFCC00] text-black ring-2 ring-[#FFCC00]/50 scale-[1.02]'
                    : isDark
                      ? 'bg-white/5 text-white/80 hover:bg-white/10 border border-white/10'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
                data-testid={`topup-${opt}`}
              >
                ${opt}
              </button>
            ))}
            <button
              onClick={() => setIsCustom(true)}
              className={`py-3 px-2 rounded-xl text-sm font-bold transition-all ${
                isCustom
                  ? 'bg-[#FFCC00] text-black ring-2 ring-[#FFCC00]/50'
                  : isDark
                    ? 'bg-white/5 text-white/80 hover:bg-white/10 border border-white/10'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
              data-testid="topup-custom"
            >
              Custom
            </button>
          </div>

          {isCustom && (
            <div className="relative">
              <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-lg font-bold ${isDark ? 'text-white/50' : 'text-gray-400'}`}>$</span>
              <input
                type="number"
                min="5"
                max="500"
                step="0.01"
                placeholder="5.00 - 500.00"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                className={`w-full pl-8 pr-4 py-3 rounded-xl text-lg font-semibold ${
                  isDark
                    ? 'bg-white/5 border border-white/10 text-white placeholder:text-white/30'
                    : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400'
                }`}
                autoFocus
                data-testid="custom-amount-input"
              />
            </div>
          )}

          {/* Overage rates info */}
          <div className={`rounded-xl p-3 ${isDark ? 'bg-white/5 border border-white/5' : 'bg-gray-50 border border-gray-100'}`}>
            <p className={`text-xs font-semibold mb-2 flex items-center gap-1.5 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              <Zap className="w-3.5 h-3.5" />
              Overage rates (when plan limits exceeded)
            </p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>SMS</p>
              <p className={`text-xs font-medium ${isDark ? 'text-white/70' : 'text-gray-600'}`}>${overageRates.sms_rate?.toFixed(2)}/msg</p>
              <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Call minutes</p>
              <p className={`text-xs font-medium ${isDark ? 'text-white/70' : 'text-gray-600'}`}>${overageRates.minutes_rate?.toFixed(2)}/min</p>
              <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Phone line</p>
              <p className={`text-xs font-medium ${isDark ? 'text-white/70' : 'text-gray-600'}`}>${overageRates.phone_line_setup?.toFixed(2)} setup</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`p-5 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <button
            onClick={handleAddFunds}
            disabled={loading || amount < 5}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-[#FFCC00] text-black font-bold text-sm hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="confirm-add-funds-btn"
          >
            {loading ? (
              <span className="animate-pulse">Processing...</span>
            ) : (
              <>
                <CreditCard className="w-4 h-4" />
                Add ${amount >= 5 ? amount.toFixed(2) : '0.00'} to Balance
              </>
            )}
          </button>
          <p className={`text-center text-xs mt-3 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
            Powered by Stripe. Secure payment.
          </p>
        </div>
      </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddFundsModal;
